import usePrivateLink from "../../../config/usePrivateLink";
import React, { useEffect, useMemo, useState } from "react";
import ".././ProgressCard.css";
import * as XLSX from "xlsx";
import TestLayout from ".././TestLayout";
import { useLocation } from "react-router-dom";
import { viewlist } from "../../../../api/BatchService";
import Header from "../../../master/SideBar";
import Nav from "../../../master/NavBar";
import { LoadingDiv, SweetAlertShow } from "../../../master/Master";
import { getSessions } from "../../../../api/SessionsService";
import Footer from "../../footer";
import { fetchCard, testList } from "../../../../api/TestServiceV2";
import {
  printProgressCards,
  buildProgressCardsHtml,
  getProgressCardsCss,
  PROGRESS_PER_PAGE_OPTIONS,
  DEFAULT_SHOW,
} from "../../../master/PrintProgressCards";
import {
  printProgressTable,
  buildProgressTableHtml,
  getProgressTableCss,
  sortProgressStudents,
  getProgressStatus,
  getProgressPercent,
  getProgressSubjects,
} from "../../../master/PrintProgressTable";

const SWITCHES = {
  card: [
    ["rank", "Rank"],
    ["classTeacher", "Class Teacher sign"],
    ["parent", "Parent sign"],
    ["principal", "Principal sign"],
    ["signatureImg", "Principal signature image"],
  ],
  table: [
    ["rank", "Rank"],
    ["classTeacher", "Class Teacher sign"],
    ["examIncharge", "Exam In-charge sign"],
    ["principal", "Principal sign"],
    ["signatureImg", "Principal signature image"],
  ],
};

export default function ProgessCard() {
  const hasAccess = usePrivateLink();
  const location = useLocation();

  const [selectedBatch, setSelectedBatch] = useState("");
  const [testListData, setTestListData] = useState("");
  const [studentSel, setStudentSel] = useState([]);

  const [batchStudents, setBatchStudents] = useState([]);
  const [batch, setBatch] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSessions] = useState("");

  const [List, setList] = useState([]);
  const [name, setName] = useState("");

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [type, setType] = useState("card");
  const [showCards, setShowCards] = useState(false);
  const [print, setPrint] = useState("");

  // number of progress cards on a single A4 page (preview + print)
  const [perPage, setPerPage] = useState(4);

  const [show, setShow] = useState({ ...DEFAULT_SHOW, examIncharge: true });

  const toggleShow = (key) =>
    setShow((prev) => ({ ...prev, [key]: !prev[key] }));

  const queryParams = new URLSearchParams(location.search);
  const details = queryParams.get("details");

  /* -------------------------------------------------------
     SESSION
  ------------------------------------------------------- */

  const getSession = async () => {
    setLoading(true);

    try {
      const response = await getSessions();

      if (response?.data?.status) {
        const sessionData = response?.data?.data || [];

        const activeSession = sessionData.find(
          (session) => String(session.status) === "1",
        );

        setSessions(sessionData);

        if (activeSession) {
          setSelectedSessions(activeSession.id);
        }
      } else {
        setError("Failed to fetch sessions.");
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getSession();
  }, []);

  /* -------------------------------------------------------
     BATCH
  ------------------------------------------------------- */

  const fetchBatches = async () => {
    if (!selectedSession) return;

    setLoading(true);

    try {
      const addUrl = `&session_id=${selectedSession}`;
      const response = await viewlist(addUrl);

      if (response?.data?.status) {
        setBatch(response.data);
      } else {
        SweetAlertShow("warning", "Warning", "Failed to fetch batches");
      }
    } catch (err) {
      SweetAlertShow(
        "error",
        "Error",
        err?.response?.data?.message || "Error fetching batches",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedSession) {
      fetchBatches();
    }
  }, [selectedSession]);

  /* -------------------------------------------------------
     TEST LIST
  ------------------------------------------------------- */

  const fetchList = async () => {
    if (!selectedBatch) return;

    setLoading(true);

    try {
      const addUrl = `&page=${page}&batch_id=${selectedBatch}&search=${search}`;

      const response = await testList(addUrl);

      if (response?.data?.status) {
        setList(response?.data?.data || []);
      }
    } catch (err) {
      SweetAlertShow(
        "error",
        "Error",
        err?.response?.data?.message ??
          err?.response?.message ??
          err?.message ??
          "Something went wrong",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedBatch) {
      fetchList();
    }
  }, [search, selectedBatch, page]);

  /* -------------------------------------------------------
     STUDENTS
  ------------------------------------------------------- */

  const fetchStudents = async () => {
    if (!testListData || !selectedBatch) return;

    setLoading(true);

    try {
      const addUrl = `&test_id=${testListData}&batch_id=${selectedBatch}`;

      const response = await fetchCard(addUrl);

      if (response?.data?.status) {
        const marksList = response?.data?.data?.marks || [];

        setBatchStudents(marksList);
        setName(response?.data?.data || {});
      } else {
        setBatchStudents([]);

        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "No student data found",
        );
      }
    } catch (err) {
      SweetAlertShow(
        "error",
        "Error",
        err?.response?.data?.message ?? err?.message ?? "Something went wrong",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (testListData) {
      fetchStudents();
      setStudentSel([]);
      setShowCards(false);
      setPrint("");
    }
  }, [testListData, selectedBatch]);

  /* -------------------------------------------------------
     FILTERED + SORTED STUDENTS

     API fields used per student:
       is_absent        "1" -> absent (marks come as "0", rank null)
       obtained_total   null -> marks not entered yet
       rank             number | null
       subject_details  [{ name, subject_marks, obtained }]
       student_details  { name, father_name, registration_no, ... }

     Order: ranked (1,2,3...) -> marks not entered -> absent (A-Z)
  ------------------------------------------------------- */

  const selectedStudents = useMemo(() => {
    if (!batchStudents?.length) return [];

    if (studentSel.includes("All")) {
      return [...batchStudents];
    }

    return batchStudents.filter((student) =>
      studentSel.includes(String(student?.student_details?.id)),
    );
  }, [batchStudents, studentSel]);

  const sortedStudents = useMemo(
    () => sortProgressStudents(selectedStudents),
    [selectedStudents],
  );

  /* -------------------------------------------------------
     PREVIEW / PRINT OPTIONS (same design for both)
  ------------------------------------------------------- */

  const sessionName = useMemo(
    () =>
      sessions?.find((s) => String(s.id) === String(selectedSession))
        ?.session || "",
    [sessions, selectedSession],
  );

  // ---- cards ----
  const cardOptions = useMemo(
    () => ({
      students: selectedStudents,
      testTitle: name?.title,
      batchTitle: name?.batches?.title,
      sessionName,
      perPage,
      show,
    }),
    [selectedStudents, name, sessionName, perPage, show],
  );

  const cardsPreviewHtml = useMemo(() => {
    if (type !== "card" || !showCards) return "";
    return buildProgressCardsHtml(cardOptions);
  }, [type, showCards, cardOptions]);

  // ---- table ----
  const tableOptions = useMemo(
    () => ({
      students: sortedStudents,
      testTitle: name?.title,
      batchTitle: name?.batches?.title,
      sessionName,
      show,
    }),
    [sortedStudents, name, sessionName, show],
  );

  const tablePreviewHtml = useMemo(() => {
    if (type !== "table" || !showCards) return "";
    return buildProgressTableHtml(tableOptions);
  }, [type, showCards, tableOptions]);

  /* -------------------------------------------------------
     HANDLERS
  ------------------------------------------------------- */

  const handleStudentChange = (e) => {
    const values = Array.from(
      e.target.selectedOptions,
      (option) => option.value,
    );

    setStudentSel(values);
    setPrint("1");
    setShowCards(false);
  };

  const handleSubmit = () => {
    if (!selectedBatch) {
      SweetAlertShow("warning", "Missing Batch", "Please select a batch.");
      return;
    }

    if (!testListData) {
      SweetAlertShow("warning", "Missing Test", "Please select a test.");
      return;
    }

    if (!studentSel.length) {
      SweetAlertShow(
        "warning",
        "No Students Selected",
        "Please select at least one student.",
      );
      return;
    }

    if (!selectedStudents.length) {
      SweetAlertShow(
        "warning",
        "No Data",
        "No student result data is available.",
      );
      return;
    }

    setShowCards(true);

    setTimeout(() => {
      document.getElementById("generated-report")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  };

  const handlePrintCards = () => {
    printProgressCards(cardOptions);
  };

  const handlePrintTable = () => {
    printProgressTable(tableOptions);
  };

  const handleExcelExport = () => {
    if (!sortedStudents?.length) {
      alert("No data to export");
      return;
    }

    // union of subjects — students can have different subjects
    const subjects = getProgressSubjects(sortedStudents);

    const header = [
      "Sr #",
      "Name",
      "Father Name",
      "Mother Name",
      "DOB",
      "Reg No",
      ...subjects.map((s) => `${s.name} (${s.max})`),
      "Total Obtained",
      "Total Marks",
      "Percentage",
      "Rank",
      "Status",
    ];

    const table = [header];

    sortedStudents.forEach((student, idx) => {
      const status = getProgressStatus(student);
      const isAppeared = status === "appeared";

      table.push([
        idx + 1,
        student?.student_details?.name || "",
        student?.student_details?.father_name || "",
        student?.student_details?.mother_name || "",
        student?.student_details?.dob || "",
        student?.student_details?.registration_no || "",

        ...subjects.map((sub) => {
          const own = (student?.subject_details || []).find(
            (x) => String(x?.id ?? x?.name) === sub.key,
          );
          if (!own) return "N/A";
          if (status === "absent" || own?.obtained === "A") return "AB";
          return own?.obtained ?? "-";
        }),

        isAppeared ? student?.obtained_total : "-",
        student?.total_marks ?? "-",
        isAppeared ? Number(getProgressPercent(student).toFixed(1)) : "-",
        isAppeared ? (student?.rank ?? "-") : "-",
        status === "appeared"
          ? "Appeared"
          : status === "absent"
            ? "Absent"
            : "Not entered",
      ]);
    });

    const worksheet = XLSX.utils.aoa_to_sheet(table);

    worksheet["!cols"] = header.map((_, i) => ({
      wch: i >= 1 && i <= 3 ? 26 : i === 4 || i === 5 ? 12 : 14,
    }));

    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Progress Report");

    XLSX.writeFile(workbook, `Progress_Report_${name?.title || "Report"}.xlsx`);
  };

  return (
    <>
      {loading && <LoadingDiv />}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />

          <div className="layout-page">
            <Nav />

            <div className="content-wrapper">
              <div className="container-fluid">
                <div className="row">
                  <TestLayout />

                  <div className="col-12">
                    {/* =====================================================
                        PAGE HEADER
                    ====================================================== */}

                    <div className="card progress-page-header mb-4">
                      <div className="progress-header-content">
                        <div className="progress-title-wrapper">
                          <div className="progress-title-icon">
                            <i className="ri-bar-chart-grouped-line"></i>
                          </div>

                          <div>
                            <h3 className="progress-page-title">
                              Progress Card Generator
                            </h3>

                            <p className="progress-page-subtitle">
                              Generate student progress cards, consolidated
                              result reports and downloadable Excel reports.
                            </p>
                          </div>
                        </div>

                        <div className="progress-student-count">
                          <span className="count-icon">
                            <i className="ri-group-line"></i>
                          </span>

                          <div>
                            <strong>{batchStudents.length}</strong>

                            <span>Students</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* =====================================================
                        FILTER PANEL
                    ====================================================== */}

                    <div className="card progress-filter-card mb-4">
                      <div className="progress-section-heading">
                        <div className="section-icon">
                          <i className="ri-filter-3-line"></i>
                        </div>

                        <div>
                          <h5>Report Configuration</h5>
                          <p>
                            Select session, batch, test and students to generate
                            the report.
                          </p>
                        </div>
                      </div>

                      <div className="row g-4">
                        {/* Session */}

                        <div className="col-xl-3 col-md-6">
                          <div className="progress-field">
                            <label>
                              <i className="ri-calendar-line"></i>
                              Academic Session
                            </label>

                            <select
                              className="form-select"
                              value={selectedSession}
                              onChange={(e) =>
                                setSelectedSessions(e.target.value)
                              }
                            >
                              <option value="">Select Session</option>

                              {sessions?.map((session) => (
                                <option key={session?.id} value={session?.id}>
                                  {session?.session}
                                  {String(session?.status) === "1"
                                    ? " — Active"
                                    : " — Inactive"}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        {/* Batch */}

                        <div className="col-xl-3 col-md-6">
                          <div className="progress-field">
                            <label>
                              <i className="ri-stack-line"></i>
                              Batch
                            </label>

                            <select
                              className="form-select"
                              value={selectedBatch}
                              onChange={(e) => setSelectedBatch(e.target.value)}
                            >
                              <option value="">Select Batch</option>

                              {batch?.data?.map((item) => (
                                <option key={item?.id} value={item?.id}>
                                  {item?.title}{" "}
                                  {item?.course?.title
                                    ? `(${item.course.title})`
                                    : ""}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        {/* Test */}

                        <div className="col-xl-3 col-md-6">
                          <div className="progress-field">
                            <label>
                              <i className="ri-file-list-3-line"></i>
                              Test
                            </label>

                            <select
                              className="form-select"
                              value={testListData}
                              onChange={(e) => setTestListData(e.target.value)}
                              disabled={!selectedBatch}
                            >
                              <option value="">Select Test</option>

                              {List?.data?.map((test) => (
                                <option key={test?.id} value={test?.id}>
                                  {test?.title}

                                  {test?.series?.title
                                    ? ` (${test.series.title})`
                                    : ""}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        {/* Students */}

                        <div className="col-xl-3 col-md-6">
                          <div className="progress-field">
                            <label>
                              <i className="ri-user-line"></i>
                              Students
                            </label>

                            <select
                              className="form-select student-select"
                              multiple
                              value={studentSel}
                              onChange={handleStudentChange}
                              disabled={!testListData}
                            >
                              <option value="All">✓ All Students</option>

                              {batchStudents?.map((student) => (
                                <option
                                  key={student?.student_details?.id}
                                  value={student?.student_details?.id}
                                >
                                  {student?.student_details?.name}
                                </option>
                              ))}
                            </select>

                            <small className="field-help">
                              Hold Ctrl / Command to select multiple students.
                            </small>
                          </div>
                        </div>
                      </div>

                      {/* Configuration summary */}

                      <div className="selection-summary mt-4">
                        <div className="summary-item">
                          <span>
                            <i className="ri-calendar-check-line"></i>
                            Session
                          </span>

                          <strong>{sessionName || "—"}</strong>
                        </div>

                        <div className="summary-item">
                          <span>
                            <i className="ri-stack-line"></i>
                            Batch
                          </span>

                          <strong>
                            {batch?.data?.find(
                              (b) => String(b.id) === String(selectedBatch),
                            )?.title || "—"}
                          </strong>
                        </div>

                        <div className="summary-item">
                          <span>
                            <i className="ri-file-chart-line"></i>
                            Test
                          </span>

                          <strong>
                            {List?.data?.find(
                              (t) => String(t.id) === String(testListData),
                            )?.title || "—"}
                          </strong>
                        </div>

                        <div className="summary-item">
                          <span>
                            <i className="ri-group-line"></i>
                            Selected
                          </span>

                          <strong>
                            {studentSel.includes("All")
                              ? `${batchStudents.length} Students`
                              : `${studentSel.length} Students`}
                          </strong>
                        </div>
                      </div>

                      <div className="generate-section">
                        <button
                          className="btn btn-primary generate-btn"
                          onClick={handleSubmit}
                        >
                          <i className="ri-magic-line"></i>
                          Generate Report
                          <i className="ri-arrow-right-line"></i>
                        </button>
                      </div>
                    </div>

                    {/* =====================================================
                        VIEW OPTIONS
                    ====================================================== */}

                    {print === "1" && (
                      <div
                        id="generated-report"
                        className="report-preview-panel mb-4"
                      >
                        {/* LEFT — TITLE */}
                        <div className="report-preview-info">
                          <div className="report-preview-icon">
                            <i className="ri-layout-2-line"></i>
                          </div>

                          <div className="report-preview-text">
                            <h5>Report Preview</h5>
                            <p>
                              View, print or export the generated student
                              results.
                            </p>
                          </div>
                        </div>

                        {/* RIGHT — ACTIONS */}
                        <div className="report-preview-actions">
                          {/* VIEW SWITCH */}
                          <div className="report-view-switch">
                            <button
                              type="button"
                              className={type === "card" ? "active" : ""}
                              onClick={() => setType("card")}
                            >
                              <i className="ri-layout-grid-line"></i>
                              <span>Cards</span>
                            </button>

                            <button
                              type="button"
                              className={type === "table" ? "active" : ""}
                              onClick={() => setType("table")}
                            >
                              <i className="ri-table-2"></i>
                              <span>Table</span>
                            </button>
                          </div>

                          {/* TABLE ACTIONS */}
                          {type === "table" && (
                            <div className="report-export-actions">
                              {hasAccess("/tests/report/print") && (
                              // {hasAccess("/progress/card/print") && (
                                <button
                                  type="button"
                                  className="report-action-btn print-btn"
                                  onClick={handlePrintTable}
                                >
                                  <i className="ri-printer-line"></i>
                                  <span>Print</span>
                                </button>
                              )}

                              {/* {hasAccess("/progress/card/export") && ( */}
                              {hasAccess("/tests/obtained/marks/export") && (
                                <button
                                  type="button"
                                  className="report-action-btn excel-btn"
                                  onClick={handleExcelExport}
                                >
                                  <i className="ri-file-excel-2-line"></i>
                                  <span>Excel</span>
                                </button>
                              )}
                            </div>
                          )}

                          {/* CARD ACTIONS */}
                          {type === "card" && (
                            <div className="d-flex align-items-center gap-2">
                              <select
                                className="form-select form-select-sm"
                                style={{ width: 125 }}
                                value={perPage}
                                onChange={(e) =>
                                  setPerPage(Number(e.target.value))
                                }
                                title="Cards per page"
                              >
                                {PROGRESS_PER_PAGE_OPTIONS.map((n) => (
                                  <option key={n} value={n}>
                                    {n} per page
                                  </option>
                                ))}
                              </select>

                              {hasAccess("/progressCard/print") && (
                                <button
                                  type="button"
                                  className="btn btn-info"
                                  onClick={handlePrintCards}
                                >
                                  <i className="ri-printer-line me-1"></i>
                                  Print
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {print === "1" && (
                      <div className="card mb-4 px-3 py-2">
                        <div className="d-flex flex-wrap align-items-center gap-4">
                          <strong style={{ fontSize: 14 }}>
                            {type === "card"
                              ? "Show on card:"
                              : "Show on table:"}
                          </strong>

                          {SWITCHES[type].map(([key, label]) => {
                            const disabled =
                              key === "signatureImg" && !show.principal;

                            return (
                              <div
                                className="form-check form-switch m-0"
                                key={key}
                              >
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  role="switch"
                                  id={`show-${key}`}
                                  checked={!!show[key] && !disabled}
                                  disabled={disabled}
                                  onChange={() => toggleShow(key)}
                                />
                                <label
                                  className="form-check-label"
                                  htmlFor={`show-${key}`}
                                >
                                  {label}
                                </label>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* =====================================================
                        CARD VIEW — preview is identical to the print design
                    ====================================================== */}

                    {type === "card" && showCards && (
                      <div className="pcp-preview-wrap">
                        <style>{getProgressCardsCss(perPage)}</style>
                        <style>{`
                          .pcp-preview-wrap {
                            overflow-x: auto;
                            padding: 16px;
                            border-radius: 12px;
                            background: #e9ecf1;
                          }
                          .pcp-preview-wrap .pcp .page {
                            width: 210mm;
                            height: 297mm;
                            padding: 8mm;
                            margin: 0 auto 16px;
                            box-shadow: 0 2px 10px rgba(0, 0, 0, 0.18);
                          }
                          .pcp-preview-wrap .pcp .page:last-child {
                            margin-bottom: 0;
                          }
                        `}</style>

                        <div
                          className="pcp"
                          dangerouslySetInnerHTML={{
                            __html: cardsPreviewHtml,
                          }}
                        />
                      </div>
                    )}

                    {/* =====================================================
                        TABLE VIEW — preview is identical to the print design
                    ====================================================== */}

                    {type === "table" && showCards && (
                      <div className="ptp-preview-wrap">
                        <style>{getProgressTableCss()}</style>
                        <style>{`
                          .ptp-preview-wrap {
                            overflow-x: auto;
                            padding: 16px;
                            border-radius: 12px;
                            background: #e9ecf1;
                          }
                          .ptp-preview-wrap .ptp {
                            min-width: 900px;
                            padding: 16px;
                            border-radius: 8px;
                            box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);
                          }
                          .ptp-preview-wrap .ptp .tbl-wrap {
                            max-height: 70vh;
                            overflow: auto;
                          }
                          .ptp-preview-wrap .ptp th {
                            position: sticky;
                            top: 0;
                            z-index: 2;
                          }
                        `}</style>

                        <div
                          dangerouslySetInnerHTML={{
                            __html: tablePreviewHtml,
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <Footer />

              <div className="content-backdrop fade"></div>
            </div>
          </div>

          <div className="layout-overlay layout-menu-toggle"></div>

          <div className="drag-target"></div>
        </div>
      </div>
    </>
  );
}
